import { Injectable, isDevMode } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { BroadcastingsDaoService } from 'src/app/dao/broadcastings-dao-service/broadcastings-dao.service';
import { Schedule } from 'src/app/models/schedule-models/schedule.model';
import { ToNeutralDatePipe } from 'src/app/pipes/to-neutral-date-pipe/to-neutral-date.pipe';
import { FromNeutralDatePipe } from 'src/app/pipes/from-neutral-date-pipe/from-neutral-date.pipe';
import { BroadcastingBasic } from 'src/app/models/broadcastings-models/broadcasting-basic.model';
import { BroadcastingInfo } from 'src/app/models/broadcastings-models/broadcasting-info.model';
import { DeviceBasic } from 'src/app/models/device-models/device-basic-model/device-basic.model';
import { BroadcastingElementBasic } from 'src/app/models/broadcastings-models/broadcasting-element-basic.model';
import { TvFormat } from 'src/app/models/broadcastings-models/tvformat.model';
import { TvLayout } from 'src/app/models/broadcastings-models/tvlayout.model';
import { UserService } from 'src/app/services/user-service/user.service';
import { SocialNetwork } from 'src/app/models/broadcastings-models/socialnetwork.model';
import { Desk } from 'src/app/models/turns-models/desk.model';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { UserStorageService } from '../user-storage-service/user-storage.service';
import { map } from 'rxjs/internal/operators/map';
import { tap } from 'rxjs/internal/operators/tap';
import { catchError } from 'rxjs/operators';

@Injectable()
export class BroadcastingsService {

  accessToken = ['BRO', 'DHB'];
  refreshBroadcastingsListener = new Subject<any>();
  refreshBroadcastingListener = new Subject<any>();
  refreshTvFormatListener = new Subject<any>();
  filterBroadcastingsListener = new Subject<any>();
  searchBroadcastingsListener = new Subject<any>();
  rememberedSchedules = new Array<Schedule>();
  toggleBroadcastingsDispositionListener = new Subject<any>();
  refreshConfigListener = new Subject<any>();
  rememberedBroadcastingForm;
  rememberedBroadcastingSelectedDevices;
  rememberedBroadcastingGallery;

  constructor(private broadcastingsDaoService: BroadcastingsDaoService,
              private userStorageService: UserStorageService,
              private toNeutralDatePipe: ToNeutralDatePipe,
              private fromNeutralDatePipe: FromNeutralDatePipe,
              private userService: UserService) { }

  public getBroadcastingsCounts() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingsCounts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            availableBroadcastings: o['data']['counters']['nbAvailables'],
            unavailableBroadcastings: o['data']['counters']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastings() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastings(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const broadcastings = new Array<BroadcastingBasic>();
          o['data']['broadcastingList'].forEach(
            (broadcasting) => {
              broadcastings.push(Object.assign(new BroadcastingBasic(), broadcasting));
            }
          );
          return broadcastings;
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastingInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const broadcasting = new BroadcastingInfo();
            Object.assign(broadcasting, o['data']['broadcastingDetails']);
            const broadcastingCalendars = new Array<Schedule>();
            o['data']['broadcastingDetails']['broadcastingCalendars'].forEach(
              (s) => {
                const schedule = new Schedule();
                Object.assign(schedule, s);
                broadcastingCalendars.push(schedule);
              }
            );
            const broadcastingElements = new Array<BroadcastingElementBasic>();
            o['data']['broadcastingDetails']['broadcastingElements'].forEach(
              (e) => {
                const broadcastingElement = new BroadcastingElementBasic();
                Object.assign(broadcastingElement, e);
                broadcastingElements.push(broadcastingElement);
              }
            );
            const broadcastingCenters = new Array<CenterLite>();
            o['data']['broadcastingDetails']['centerBound'].forEach(
              (c) => {
                const center = new CenterLite();
                Object.assign(center, c);
                broadcastingCenters.push(center);
              }
            );
            const broadcastingDevices = new Array<DeviceBasic>();
            o['data']['broadcastingDetails']['devicesBound'].forEach(
              (d) => {
                const device = new DeviceBasic();
                Object.assign(device, d);
                broadcastingDevices.push(device);
              }
            );
            broadcasting.BroadcastingCalendars = broadcastingCalendars;
            broadcasting.BroadcastingElements = broadcastingElements;
            broadcasting.DevicesBound = broadcastingDevices;
            broadcasting.CenterBound = broadcastingCenters;
            return broadcasting;
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastingExistingSchedules(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      const internalIds = internalId.map(i => i.InternalId);
      return this.broadcastingsDaoService.getBroadcastingExistingSchedules(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedules = new Array<Schedule>();
          o['data']['calendarList'].forEach(
            (ct) => {
              const schedule = new Schedule();
              Object.assign(schedule, ct);
              schedules.push(schedule);
            }
          );
          return schedules;
        })
      );
    }
    return EMPTY;
  }

  public addNewBroadcasting(active, broadcastingName, centerBound, devicesBound) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.addNewBroadcasting(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, broadcastingName, centerBound, devicesBound).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public addNewBroadcastingSchedule(relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.addNewBroadcastingSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateBroadcastingInfo(internalId, active, broadcastingName, showApp) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, broadcastingName, showApp).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateBroadcastingSchedule(internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId, mon, tue, wed, thu, fri, sat, sun, openTimeMorning, closeTimeMorning, openTimeAfternoon, closeTimeAfternoon, startDate, endDate, openService, commentNote, timeZone).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateBroadcastingCenters(internalId, active, showApp, centerBound) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, showApp, centerBound).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateBroadcastingDevices(internalId, active, showApp, devicesBound) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, showApp, devicesBound).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateBroadcastingGallery(internalId, active, showApp, broadcastingElements) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingGallery(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, showApp, broadcastingElements).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public linkExistingBroadcastingSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.linkExistingBroadcastingSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteBroadcasting(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.deleteBroadcasting(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteBroadcastingSchedule(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.deleteBroadcastingSchedule(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getBroadcastingDevices(internalIdList) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalIdList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const devices = new Array<DeviceBasic>();
          o['data']['deviceList'].forEach(
            (d) => {
              const device = new DeviceBasic();
              Object.assign(device, d);
              devices.push(device);
            }
          );
          return devices;
        })
      );
    }
    return EMPTY;
  }

  public updateBroadcastingStatus(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getTvFormatDevicesCounts() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getTvFormatDevicesCounts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            formatDevicesAvailables: o['data']['counters']['nbAvailables'],
            formatDevicesUnavailables: o['data']['counters']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }
  public getTvFormatDevices() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getTvFormatDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tvFormats = new Array<TvFormat>();
          o['data']['tvConfigList'].forEach(
            (tvf) => {
              const tvFormat = new TvFormat();
              const socialNetworks = new Array<SocialNetwork>();
              const tvLayout = new TvLayout();
              Object.assign(tvFormat, tvf);
              tvf['socialNetworkList'].forEach(
                (sn) => {
                  const socialNetwork = new SocialNetwork();
                  Object.assign(socialNetwork, sn);
                  socialNetworks.push(socialNetwork);
                }
              );
              Object.assign(tvLayout, tvf['tvLayout']);
              tvFormat.TvLayout = tvLayout;
              tvFormat.SocialNetworkList = socialNetworks;
              tvFormats.push(tvFormat);
            }
          );
          return tvFormats;
        })
      );
    }
    return EMPTY;
  }

  public getTvFormatDeviceInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getTvFormatDeviceInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tvFormat = new TvFormat();
          const socialNetworks = new Array<SocialNetwork>();
          const tvLayout = new TvLayout();
          Object.assign(tvFormat, o['data']['tvConfig']);
          o['data']['tvConfig']['socialNetworkList'].forEach(
            (sn) => {
              const socialNetwork = new SocialNetwork();
              Object.assign(socialNetwork, sn);
              socialNetworks.push(socialNetwork);
            }
          );
          Object.assign(tvLayout, o['data']['tvConfig']['tvLayout']);
          tvFormat.TvLayout = tvLayout;
          tvFormat.SocialNetworkList = socialNetworks;
          return tvFormat;
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastingScheduleInfo(internalId, relatedEntityInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingScheduleInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, relatedEntityInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const schedule = new Schedule();
          Object.assign(schedule, o['data']['broadcastingCalendar']);
          return schedule;
        })
      );
    }
    return EMPTY;
  }

  public getTvFormatLayouts(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getTvFormatLayouts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tvConfigLayouts = new Array<TvLayout>();
          o['data']['tvFormatShapes']['tvLayoutList'].forEach(
            (l) => {
              const tvConfigLayout = new TvLayout();
              Object.assign(tvConfigLayout, l);
              tvConfigLayouts.push(tvConfigLayout);
            }
          );
          return tvConfigLayouts;
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastingLayouts() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingLayouts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tvConfigLayouts = new Array<TvLayout>();
          o['data'].forEach(
            (l) => {
              const tvConfigLayout = new TvLayout();
              Object.assign(tvConfigLayout, l);
              tvConfigLayouts.push(tvConfigLayout);
            }
          );
          return tvConfigLayouts;
        })
      );
    }
    return EMPTY;
  }

  public getBroadcastCenterConfig(centerInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastCenterConfig(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        centerInternalId
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['config'])
      );
    }
    return EMPTY;
  }

  public updateBroadcastCenterConfig(
    centerInternalId,
    layoutInternalId,
    backgroundColor,
    textPrimaryColor,
    textSecondaryColor,
    overrideDevicesValues,
    showOfflineChip
  ) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastCenterConfig(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        centerInternalId,
        layoutInternalId,
        backgroundColor,
        textPrimaryColor,
        textSecondaryColor,
        overrideDevicesValues,
        showOfflineChip
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data'])
      );
    }
    return EMPTY;
  }

  public getTvFormatDesks(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getTvFormatDesks(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const tvConfigDesks = new Array<Desk>();
          o['data']['tvFormatShapes']['deskList'].forEach(
            (d) => {
              const tvConfigDesk = new Desk();
              Object.assign(tvConfigDesk, d);
              tvConfigDesks.push(tvConfigDesk);
            }
          );
          return tvConfigDesks;
        })
      );
    }
    return EMPTY;
  }

  public updateTvFormatLayout(internalId, tvLayoutInternalId, checkPrincipalDesk, principalDeskInternalId, backgroundColour, textPrimaryColour, textSecondaryColour) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateTvFormatLayout(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, tvLayoutInternalId, checkPrincipalDesk, principalDeskInternalId, backgroundColour, textPrimaryColour,  textSecondaryColour).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateTvFormatWidget(internalId, socialNetworkList, checkPrincipalDesk, principalDeskInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateTvFormatWidget(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, socialNetworkList, checkPrincipalDesk, principalDeskInternalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public forceBroadcastingUpdate(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.forceBroadcastingUpdate(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public forceAllBroadcastingsUpdate() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.forceAllBroadcastingsUpdate(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public getBroadcastingGridDeviceView(dateType, centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingGridDeviceView(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), dateType, centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['grid'])
      );
    }
    return EMPTY;
  }

  public getBroadcastingGridBroadcastingView(dateType, centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingGridBroadcastingView(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), dateType, centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['grid'])
      );
    }
    return EMPTY;
  }
  public getBroadcastingsConfig() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.getBroadcastingsConfig(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['config'])
      );
    }
    return EMPTY;
  }
  public updateBroadcastingsAssignmentModeConfig(assignmentMode) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastingsAssignmentModeConfig(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        assignmentMode
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data'])
      );
    }
    return EMPTY;
  }

  public updateBroadcastCenterConfigBackgroundImage(centerInternalId, backgroundImageUrl) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.broadcastingsDaoService.updateBroadcastCenterConfigBackgroundImage(
        this.userStorageService.getContext(),
        this.userStorageService.getLang(),
        this.userStorageService.getUDID(),
        centerInternalId,
        backgroundImageUrl
      ).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public toNeutralDate(date) {
    return this.toNeutralDatePipe.transform(date);
  }
  public fromNeutralDate(date) {
    return this.fromNeutralDatePipe.transform(date);
  }
  public rememberbroadcastingInfo(broadcastingForm, selectedDevices, gallery) {
    this.rememberedBroadcastingForm = broadcastingForm;
    this.rememberedBroadcastingSelectedDevices = selectedDevices;
    this.rememberedBroadcastingGallery = gallery;
  }
  public getRememberedBroadcastingInfo() {
    return { rememberedBroadcastingForm: this.rememberedBroadcastingForm,
             rememberedBroadcastingSelectedDevices: this.rememberedBroadcastingSelectedDevices,
             rememberedBroadcastingGallery: this.rememberedBroadcastingGallery };
  }
  public toRememberedSchedules(schedule) {
    this.rememberedSchedules.push(schedule);
  }
  public rememberSchedules(schedules) {
    this.rememberedSchedules = schedules;
  }
  public getRememberedSchedules() {
    return this.rememberedSchedules;
  }
  public deleteRememberedSchedule(schedule) {
    this.rememberedSchedules.splice(
      this.rememberedSchedules.findIndex(
        (s) => {
          return schedule.CommentNote === s.CommentNote;
        }
      ), 1);
  }
  public deleteAllRememberedSchedule() {
    this.rememberedSchedules = new Array<Schedule>();
  }
  public refreshBroadcastings() {
    this.refreshBroadcastingsListener.next();
  }
  public refreshBroadcastingsListen() {
    return this.refreshBroadcastingsListener.asObservable();
  }
  public refreshBroadcasting() {
    this.refreshBroadcastingListener.next();
  }
  public refreshBroadcastingListen() {
    return this.refreshBroadcastingListener.asObservable();
  }
  public refreshTvFormat() {
    this.refreshTvFormatListener.next();
  }
  public refreshTvFormatListen() {
    return this.refreshTvFormatListener.asObservable();
  }
  public filterBroadcastings(opts) {
    this.filterBroadcastingsListener.next(opts);
  }
  public filterBroadcastingsListen() {
    return this.filterBroadcastingsListener.asObservable();
  }
  public searchBroadcastings(key) {
    this.searchBroadcastingsListener.next(key);
  }
  public searchBroadcastingsListen() {
    return this.searchBroadcastingsListener.asObservable();
  }
  public toggleBroadcastingsDisposition(viewDis) {
    this.toggleBroadcastingsDispositionListener.next(viewDis);
  }
  public toggleBroadcastingsDispositionListen() {
    return this.toggleBroadcastingsDispositionListener.asObservable();
  }

  public refreshConfig() {
    this.refreshConfigListener.next();
  }

  public refreshConfigListen() {
    return this.refreshConfigListener.asObservable();
  }
}
